<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="848"
    role="dialog"
    aria-modal="true"
    aria-labelledby="gg-drawer-mos-title"
    class="gg-idrawer"
    @input="$emit('input', $event)"
  >
    <div class="gg-idrawer__header">
      <h2 id="gg-drawer-mos-title" class="gg-idrawer__title">Importar playlist</h2>
      <button type="button" class="gg-idrawer__close" aria-label="Fechar" @click="close">
        <v-icon size="24">mdi-close</v-icon>
      </button>
    </div>

    <div class="gg-idrawer__body">
      <div v-if="loadingList" class="gg-idrawer__loading">
        <v-progress-circular indeterminate size="24" color="primary" />
      </div>

      <div v-else-if="!rundowns.length" class="sc-empty-state">
        <span class="sc-empty-state__title">Nenhum rundown encontrado</span>
      </div>

      <div v-else>
        <div v-for="(group, ncs) in rundownsByNcs" :key="ncs" class="gg-mos-group">
          <span class="gg-mos-group__title">{{ ncs }}</span>
          <div class="gg-idrawer__counter">
            <span class="gg-idrawer__count">
              {{ group.length }} disponíve{{ group.length === 1 ? 'l' : 'is' }}
            </span>
          </div>

          <div class="gg-idrawer__grid">
            <label
              v-for="r in group"
              :key="r.ncsId + '/' + r.roId"
              class="gg-idrawer__item"
              :class="{ 'gg-idrawer__item--selected': isSelected(r) }"
            >
              <input
                type="radio"
                class="gg-idrawer__control"
                name="gg-mos-rundown"
                :value="r.roId"
                :checked="isSelected(r)"
                @change="selected = r"
              />
              <span class="gg-idrawer__thumb">
                <GgFolderThumb />
                <v-icon size="32" class="gg-mos-thumb-icon">mdi-playlist-play</v-icon>
              </span>
              <span class="gg-idrawer__label">{{ r.roId }} [{{ r.ncsId }}]</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="gg-idrawer__footer">
      <button type="button" class="gg-idrawer__submit" :disabled="!canImport" @click="doImport">
        {{ importing ? 'Importando…' : 'Importar' }}
      </button>
    </div>
  </v-navigation-drawer>
</template>

<script>
/**
 * Browser for the MOS rundowns the Viz MOS Gateway publishes on the site's
 * Sequencer. Lists rundowns (GET /playout/mos/rundowns) grouped by NCS and
 * imports the selected one into a GG playlist (POST /playout/mos/import). A
 * closed/off-air rundown imports fine — the elements self-load server-side, so
 * nobody has to open it in Trio first.
 */
import { mapActions } from 'vuex'
import GgFolderThumb from '@/assets/gg_folder_thumb.svg'

import ggDrawerA11y from '@/mixins/ggDrawerA11y'

export default {
  mixins: [ggDrawerA11y],
  name: 'MosRundownPicker',
  components: { GgFolderThumb },
  props: {
    value: { type: Boolean, default: false },
    codProducao: { type: [Number, String], default: null },
  },
  data() {
    return {
      rundowns: [],
      loadingList: false,
      importing: false,
      selected: null,
    }
  },
  computed: {
    canImport() {
      return !!(this.selected && this.codProducao && !this.importing)
    },
    rundownsByNcs() {
      const map = {}
      for (const r of this.rundowns) {
        if (!map[r.ncsId]) map[r.ncsId] = []
        map[r.ncsId].push(r)
      }
      return map
    },
  },
  watch: {
    value(open) {
      if (open) this.loadRundowns()
    },
  },
  methods: {
    ...mapActions({
      fetchMosRundowns: 'playout/fetchMosRundowns',
      importMosRundown: 'playout/importMosRundown',
    }),
    close() {
      this.$emit('input', false)
    },
    isSelected(r) {
      return !!this.selected && this.selected.roId === r.roId && this.selected.ncsId === r.ncsId
    },
    async doImport() {
      if (!this.canImport) return
      this.importing = true
      try {
        const result = await this.importMosRundown({
          ncsId: this.selected.ncsId,
          roId: this.selected.roId,
          codProducao: Number(this.codProducao),
        })
        this.$toast?.success?.(`Rundown importado: ${result.imported} elemento(s) em ${result.stories} pasta(s)`)
        this.$emit('imported', result.id_playlist)
        this.close()
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.error || 'Erro ao importar rundown')
      } finally {
        this.importing = false
      }
    },
    async loadRundowns() {
      this.loadingList = true
      this.selected = null
      try {
        this.rundowns = (await this.fetchMosRundowns()) || []
      } catch (_) {
        this.rundowns = []
      } finally {
        this.loadingList = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Drawer chrome comes from _gg-import-drawer.scss; only the per-NCS grouping
// and the folder overlay are specific to this picker.
.gg-mos-group + .gg-mos-group {
  margin-top: var(--sc-space-6);
}

.gg-mos-group__title {
  display: block;
  padding: 0 var(--sc-space-2);
  font-size: 20px;
  font-weight: var(--sc-weight-semi);
  line-height: 24px;
  color: var(--sc-gg-text-tertiary);
}

.gg-mos-thumb-icon {
  position: absolute;
  top: 28px;
  left: 52px;
  color: var(--sc-gg-text-secondary) !important;
}
</style>
